import React, { memo } from 'react';
import { Box, InputBase, TextField, Typography } from '@mui/material';

interface Props {
  label: string;
  /** Completed months (2 years 6 months = 30); undefined = no limit. */
  value?: number;
  onChange: (months: number | undefined) => void;
}

const digits = (s: string, max: number): number | undefined => {
  const d = s.replace(/\D/g, '');
  return d === '' ? undefined : Math.min(parseInt(d, 10), max);
};

const Unit: React.FC<{ children: string }> = ({ children }) => (
  <Typography component="span" variant="body2" color="text.secondary" sx={{ px: 0.75, userSelect: 'none' }}>{children}</Typography>
);

const numberSx = { width: '2.4ch', textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' };

/**
 * An age limit as one field the way schools write it — "2 yrs | 6 mos".
 * Stores completed months; both parts empty means no limit.
 */
const AgeLimitInput = memo<Props>(({ label, value, onChange }) => {
  const years = value != null ? Math.floor(value / 12) : undefined;
  const months = value != null ? value % 12 : undefined;

  // Months past 11 roll into years, so "18 mos" becomes 1 yr 6 mos.
  const set = (y?: number, m?: number) =>
    onChange(y == null && m == null ? undefined : Math.min((y ?? 0) * 12 + (m ?? 0), 25 * 12 + 11));

  return (
    <TextField
      size="small"
      label={label}
      value={years || (value === 0 ? 0 : '')}
      onChange={(e) => set(digits(e.target.value, 25), months)}
      placeholder="–"
      InputLabelProps={{ shrink: true }}
      inputProps={{ inputMode: 'numeric', 'aria-label': `${label}, years`, style: numberSx }}
      InputProps={{
        endAdornment: (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Unit>yrs</Unit>
            <Box sx={{ alignSelf: 'stretch', width: '1px', my: -0.5, mx: 0.5, bgcolor: 'divider' }} />
            <InputBase
              value={months || ''}
              onChange={(e) => set(years, digits(e.target.value, 99))}
              placeholder="0"
              inputProps={{ inputMode: 'numeric', 'aria-label': `${label}, months`, style: numberSx }}
              sx={{ fontSize: 'inherit', ml: 0.5 }}
            />
            <Unit>mos</Unit>
          </Box>
        ),
      }}
      sx={{ width: 184, flexShrink: 0 }}
    />
  );
});

AgeLimitInput.displayName = 'AgeLimitInput';

export default AgeLimitInput;
